import React, { useState, useEffect, useCallback } from "react";
import "./tutorial.css";

const MOCK_ITEM_NAME = "Organic Cold-Pressed Juice";

const TUTORIAL_GUESSES = [
  { value: 6.0, feedback: "way low" },
  { value: 11.0, feedback: "low" },
  { value: 13.0, feedback: "correct" },
];

const TOTAL_STEPS = 8;

export default function Tutorial({ onClose = () => {} }) {
  const [step, setStep] = useState(0);
  const [guesses, setGuesses] = useState([]);
  const [typingValue, setTypingValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [highlightRect, setHighlightRect] = useState(null);

  const typeValue = useCallback((value, onDone) => {
    setIsTyping(true);
    setTypingValue("");
    const str = value.toFixed(2);
    let i = 0;
    const interval = setInterval(() => {
      if (i <= str.length) {
        setTypingValue(str.slice(0, i));
        i++;
      } else {
        clearInterval(interval);
        setIsTyping(false);
        if (onDone) onDone();
      }
    }, 80);
    return () => clearInterval(interval);
  }, []);

  // Highlight real UI elements when relevant
  useEffect(() => {
    setShowResult(false);
    setHighlightRect(null);

    let selector = null;
    if (step === 5) selector = ".progress-bar";
    if (step === 6) selector = ".hard-mode-wrapper";

    if (selector) {
      const el = document.querySelector(selector);
      if (el) {
        const rect = el.getBoundingClientRect();
        setHighlightRect({
          top: rect.top - 6,
          left: rect.left - 6,
          width: rect.width + 12,
          height: rect.height + 12,
        });
      }
    }
  }, [step]);

  // Auto-type for guess steps (2, 3, 4)
  useEffect(() => {
    if (step >= 2 && step <= 4) {
      const guessIndex = step - 2;
      const guess = TUTORIAL_GUESSES[guessIndex];
      const cleanup = typeValue(guess.value, () => {
        setTimeout(() => {
          setGuesses((prev) => {
            if (prev.length > guessIndex) return prev;
            return [...prev, { guess: guess.value, feedback: guess.feedback }];
          });
          setShowResult(true);
        }, 400);
      });
      return cleanup;
    }
  }, [step, typeValue]);

  const nextStep = () => {
    if (step === TOTAL_STEPS - 1) {
      onClose();
      return;
    }
    setTypingValue("");
    setShowResult(false);
    setStep((s) => s + 1);
  };

  const prevStep = () => {
    if (step <= 0) return;
    if (step >= 2 && step <= 4) {
      setGuesses((prev) => prev.slice(0, step - 2));
    }
    setTypingValue("");
    setShowResult(false);
    setStep((s) => s - 1);
  };

  const feedbackClass = (fb) => {
    switch (fb) {
      case "correct": return "correct";
      case "high": return "high";
      case "low": return "low";
      case "way high": return "way-high";
      case "way low": return "way-low";
      default: return "";
    }
  };

  const feedbackArrow = (fb) => {
    switch (fb) {
      case "correct": return "✓";
      case "high": case "way high": return "↓";
      case "low": case "way low": return "↑";
      default: return "";
    }
  };

  const isGuessStep = step >= 2 && step <= 4;
  const showMock = step >= 1 && step <= 4;

  const titles = [
    "Welcome to Erewhondle! 👋",
    "Here's Your Item",
    "Guess #1 — Way Off 🟥",
    "Guess #2 — Getting Warm 🟨",
    "Guess #3 — Nailed It! 🟩",
    "Track Your Progress",
    "🔥 Hard Mode",
    "You're All Set! 🎉",
  ];

  const descs = [
    "Let's walk through a quick example game so you know exactly how it works.",
    "You'll see a product image and name. Type a dollar amount and hit Submit. You get 6 tries per item.",
    "More than 25% away — the box turns red with an arrow showing you need to go higher.",
    "Within 25% now! The box turns yellow. The arrow still points the right direction.",
    "Within 5% (or 25¢ on cheap items): that's a win! The box turns green with a checkmark. On to the next item!",
    "The dots at the top show your 5-item progress. Green = won, red = lost. Tap any dot to jump to that item.",
    "Tap the fire button before guessing to hide direction arrows — you only see the color. Hard wins show 🔥 in your results!",
    "New puzzle every day with 5 items. Keep your streak alive and share your results! Good luck!",
  ];

  return (
    <div className="tutorial-overlay" role="presentation">
      {highlightRect && (
        <div
          className="tutorial-highlight"
          style={highlightRect}
        />
      )}

      <div
        className="tutorial-card"
        role="dialog"
        aria-modal="true"
        aria-label="Interactive tutorial"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="tutorial-close" onClick={onClose} aria-label="Skip tutorial">
          Skip ✖
        </button>

        <div className="tutorial-progress">
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <div
              key={i}
              className={`tutorial-pip ${i === step ? "active" : ""} ${i < step ? "done" : ""}`}
            />
          ))}
        </div>

        <h3 className="tutorial-title">{titles[step]}</h3>
        <p className="tutorial-body">{descs[step]}</p>

        {showMock && (
          <div className="tutorial-mock">
            <div className="tutorial-mock-product">
              <div className="tutorial-mock-image">🧃</div>
              <p className="tutorial-mock-name">{MOCK_ITEM_NAME}</p>
            </div>

            <p className="tutorial-mock-note">Just an example: it types by itself.</p>
            <div className="tutorial-mock-form" aria-hidden="true">
              <span className="tutorial-dollar">$</span>
              <div className="tutorial-mock-input">
                {typingValue || "0.00"}
                {isTyping && <span className="tutorial-cursor">|</span>}
              </div>
              <div className={`tutorial-mock-submit ${showResult ? "pressed" : ""}`}>
                Submit
              </div>
            </div>

            <div className="tutorial-mock-guesses">
              {guesses.map((g, i) => (
                <div
                  key={i}
                  className={`tutorial-mock-guess ${feedbackClass(g.feedback)}`}
                >
                  <span>${g.guess.toFixed(2)}</span>
                  <span className="tutorial-mock-arrow">{feedbackArrow(g.feedback)}</span>
                </div>
              ))}
              {Array.from({ length: Math.max(0, 3 - guesses.length) }).map((_, i) => (
                <div key={`e${i}`} className="tutorial-mock-guess empty" />
              ))}
            </div>
          </div>
        )}

        <div className="tutorial-nav">
          {step > 0 && (
            <button className="tutorial-btn secondary" onClick={prevStep}>
              ← Back
            </button>
          )}
          <button
            className="tutorial-btn primary"
            onClick={nextStep}
            disabled={isGuessStep && !showResult}
          >
            {step === TOTAL_STEPS - 1 ? "Let's Play! 🎮" : "Next →"}
          </button>
        </div>

        <p className="tutorial-step-count">
          {step + 1} / {TOTAL_STEPS}
        </p>
      </div>
    </div>
  );
}
